import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { PlainInput } from 'components/PlainInput'
import { useEffect, useMemo, useState } from 'react'
import { getPostClosingFields } from 'services'
import { Modal, Tooltip } from 'stories/components'

import { AssignorOption, AssignorProfile, normalizeAssignorOptions } from './assignorOptions'

const accountTypeLabel: Record<string, string> = {
  correspondent: 'Correspondents',
  broker: 'Brokers',
  tablefunder: 'Table Funders',
}

const accountTypeOrder = ['correspondent', 'broker', 'tablefunder']

interface IProps {
  assignmentNames: AssignorOption[]
  onOk: (arg0: AssignorOption[]) => void
}

export const AssignmentPdfSettingsModal = ({ assignmentNames, onOk }: IProps) => {
  const [values, setValues] = useState<AssignorOption[]>([])
  const [showImport, setShowImport] = useState(false)
  const [profiles, setProfiles] = useState<AssignorProfile[] | null>(null)
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const [importQuery, setImportQuery] = useState('')

  useEffect(() => {
    setValues(normalizeAssignorOptions(assignmentNames))
  }, [assignmentNames])

  const existingNames = useMemo(
    () => new Set(values.map((item) => item.name.trim().toLowerCase()).filter(Boolean)),
    [values],
  )

  const onAdd = () => {
    setValues([...values, { name: '', address: '' }])
  }

  const onUpdate = (index: number, patch: Partial<AssignorOption>) => {
    const newValues = cloneDeep(values)
    newValues[index] = { ...newValues[index], ...patch }
    setValues(newValues)
  }

  const onRemove = (index: number) => {
    const newValues = cloneDeep(values)
    newValues.splice(index, 1)
    setValues(newValues)
  }

  const openImport = () => {
    setShowImport(true)
    if (profiles) return
    getPostClosingFields('assignorProfiles').then((data) => {
      const rows = Array.isArray(data) ? (data as AssignorProfile[]) : []
      setProfiles(
        rows
          .map((row) => ({
            name: String(row?.name || '').trim(),
            address: String(row?.address || '').trim(),
            accountType: String(row?.accountType || ''),
          }))
          .filter((row) => row.name),
      )
    })
  }

  const visibleProfiles = useMemo(() => {
    const query = importQuery.trim().toLowerCase()
    return (profiles || []).filter((profile) => !query || profile.name.toLowerCase().includes(query))
  }, [profiles, importQuery])

  const groupedProfiles = useMemo(() => {
    return accountTypeOrder
      .map((accountType) => ({
        accountType,
        label: accountTypeLabel[accountType] || accountType,
        rows: visibleProfiles.filter((profile) => profile.accountType === accountType),
      }))
      .filter((group) => group.rows.length)
  }, [visibleProfiles])

  const addPicked = () => {
    const additions = (profiles || []).filter((profile) => {
      const key = profile.name.toLowerCase()
      return picked[key] && !existingNames.has(key)
    })
    if (!additions.length) return
    setValues([
      ...values.filter((item) => item.name.trim()),
      ...additions.map((profile) => ({ name: profile.name, address: profile.address })),
    ])
    setPicked({})
  }

  const onConfirm = () => {
    onOk(normalizeAssignorOptions(values))
    return true
  }

  return (
    <Modal
      title="Assignment Pdf Settings"
      titleOkay="Update"
      onOk={onConfirm}
      button={
        <Tooltip message="Assignor Entity Name">
          <span className="ml-4 p-1 rounded flex hover-shadow1 cursor-pointer text-shade-blue">
            <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
          </span>
        </Tooltip>
      }
    >
      <div className="text-gray-700 w-[48rem] max-w-[92vw]">
        <div className="flex items-center justify-between gap-3 px-6 py-3 bg-slate-50 border border-slate-200 rounded-t">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            Assignor Entity Name Options
          </span>
          <span className="flex items-center gap-4">
            <button
              type="button"
              className="text-sm font-medium text-shade-blue hover:underline"
              onClick={openImport}
            >
              Import from accounts
            </button>
            <button type="button" className="text-sm font-medium text-shade-blue hover:underline" onClick={onAdd}>
              + Add
            </button>
          </span>
        </div>

        {showImport && (
          <div className="border border-t-0 border-slate-200 px-6 py-3 bg-white">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-sm text-gray-600">Correspondents, Brokers, and Table Funders</p>
              <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setShowImport(false)}>
                Close
              </button>
            </div>
            <input
              value={importQuery}
              onChange={(event) => setImportQuery(event.target.value)}
              placeholder="Search companies"
              className="w-full mb-2 rounded border border-gray-300 px-2 py-1 text-sm focus:border-shade-blue focus:outline-none"
            />
            <div className="max-h-56 overflow-auto border border-slate-100 rounded">
              {profiles === null && <p className="px-3 py-2 text-sm text-gray-500">Loading profiles...</p>}
              {profiles && !groupedProfiles.length && (
                <p className="px-3 py-2 text-sm text-gray-500">No companies found.</p>
              )}
              {groupedProfiles.map((group) => (
                <div key={group.accountType}>
                  <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                    {group.label}
                  </p>
                  {group.rows.map((profile) => {
                    const key = profile.name.toLowerCase()
                    const alreadyAdded = existingNames.has(key)
                    return (
                      <label
                        key={`${group.accountType}-${key}`}
                        className="flex items-start gap-2 px-3 py-1.5 text-sm text-gray-800 border-t border-slate-100"
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          disabled={alreadyAdded}
                          checked={alreadyAdded || !!picked[key]}
                          onChange={(event) => setPicked({ ...picked, [key]: event.target.checked })}
                        />
                        <span>
                          <span className="font-medium">{profile.name}</span>
                          {alreadyAdded && <span className="ml-2 text-xs text-gray-400">Already added</span>}
                          <span className="block text-xs text-gray-500">{profile.address || 'No address on file'}</span>
                        </span>
                      </label>
                    )
                  })}
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <button type="button" className="text-sm font-medium text-shade-blue hover:underline" onClick={addPicked}>
                Add selected
              </button>
            </div>
          </div>
        )}

        <div className="max-h-[50vh] overflow-auto border border-t-0 border-slate-200 rounded-b">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-white border-b border-slate-200">
              <tr>
                <th scope="col" className="px-6 py-2 w-1/2">
                  Entity name
                </th>
                <th scope="col" className="px-6 py-2">
                  Address
                </th>
                <th scope="col" className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {values.map((item, index) => (
                <tr className="bg-white border-b border-slate-100" key={index}>
                  <td className="px-6 py-1 font-medium text-gray-900">
                    <PlainInput
                      value={item.name}
                      content={item.name}
                      isEditing={item.name === ''}
                      onChange={(newValue: string) => onUpdate(index, { name: newValue })}
                    />
                  </td>
                  <td className="px-6 py-1 text-gray-900">
                    <PlainInput
                      value={item.address}
                      content={item.address || 'Add address'}
                      className={item.address ? '' : 'text-gray-400'}
                      onChange={(newValue: string) => onUpdate(index, { address: newValue })}
                    />
                  </td>
                  <td className="px-3 py-1">
                    <span className="text-red-800 p-1 hover-shadow1 cursor-pointer inline-flex" onClick={() => onRemove(index)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </td>
                </tr>
              ))}
              {!values.length && (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm text-gray-500">
                    No assignors yet. Add one, or import a company from Manage Accounts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
